import type { DataEntryRecord, EmbryoOwner } from '../../types';

/**
 * Returns the label to display for an embryo owner in the Owner picker.
 *
 * Preference order:
 *   1. The owner's ranch code (account number) — matches legacy display.
 *   2. The owner's account name — readable fallback when ranch code is blank.
 *   3. A literal "(unnamed owner)" if both are blank.
 *
 * Never returns a Guid id, so the user can't see "a number" in place of a name.
 */
export function getOwnerOptionLabel(owner: EmbryoOwner): string {
  if (owner.ownerRanchCode) return owner.ownerRanchCode;
  if (owner.ownerName) return owner.ownerName;
  return '(unnamed owner)';
}

export function getSelectedOwner(
  entry: Pick<DataEntryRecord, 'embryoOwnerId' | 'embryoOwners'>,
): EmbryoOwner | undefined {
  const match = entry.embryoOwners.find(
    (o) => o.ownerId === entry.embryoOwnerId,
  );
  if (match) return match;
  return entry.embryoOwners.length === 1 ? entry.embryoOwners[0] : undefined;
}

export type BillingAccount = { id: string; name: string };

export function resolveBillingAccount({
  entry,
  current,
  syncedOwnerId,
}: {
  entry: Pick<DataEntryRecord, 'embryoOwnerId' | 'embryoOwners'>;
  current: BillingAccount;
  syncedOwnerId: string | null;
}): BillingAccount {
  const owner = getSelectedOwner(entry);
  if ((owner?.ownerId ?? '') === syncedOwnerId) return current;
  if (!owner) return { id: '', name: '' };
  return { id: owner.ownerId, name: getOwnerOptionLabel(owner) };
}
